import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import jwtDecode from 'jwt-decode';
import brandLogo from '../../assets/brandLogo.png';
import url from '../../constants/config';
import { setUserEmail, setUserRole, setIsProfileFinished } from '../../shared/store/authSlice';
import { toasterOn } from '../../shared/store/actions';

const PlayerLoginPage = ({ setIsAuthorized }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const params = new URLSearchParams(location.search);
  const inviteToken = params.get('invite');
  const inviteEmail = params.get('email') || '';

  const isSetupMode = Boolean(inviteToken);

  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);

  useEffect(() => {
    if (inviteEmail) setEmail(inviteEmail);
  }, [inviteEmail]);

  const handleLogin = async e => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const result = await axios.post(`${url}/player-auth/login`, { email, password });
      const token = result.headers.authorization;
      window.localStorage.setItem('authorization', token);
      const decoded = jwtDecode(token);
      dispatch(setUserRole('player'));
      dispatch(setIsProfileFinished(true));
      dispatch(setUserEmail(decoded.email));
      if (setIsAuthorized) setIsAuthorized(true);
      history.push('/player/inbox');
    } catch (err) {
      setError(
        err.response?.data?.message || 'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async e => {
    e.preventDefault();
    setError('');
    if (!password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const result = await axios.post(`${url}/player-auth/setup`, {
        inviteToken,
        email,
        password,
      });
      const token = result.headers.authorization;
      window.localStorage.setItem('authorization', token);
      const decoded = jwtDecode(token);
      dispatch(setUserRole('player'));
      dispatch(setIsProfileFinished(true));
      dispatch(setUserEmail(decoded.email));
      dispatch(toasterOn({ message: 'Account created! Welcome to PlaySportUSA.', type: 'success' }));
      if (setIsAuthorized) setIsAuthorized(true);
      history.push('/player/inbox');
    } catch (err) {
      setError(
        err.response?.data?.message || 'Setup failed. Your invite link may have expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async e => {
    e.preventDefault();
    setError('');
    if (!forgotEmail) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${url}/player-auth/reset-password/${encodeURIComponent(forgotEmail)}`);
      setForgotSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Could not reset password. Please check your email and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (isForgotMode) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
            padding: '40px',
            width: '100%',
            maxWidth: '400px',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img src={brandLogo} alt="PlaySportUSA" style={{ height: '40px', marginBottom: '16px' }} />
            <h2 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '20px' }}>Reset Password</h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              Enter your email and we&apos;ll send you a temporary password.
            </p>
          </div>

          {error && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          {forgotSuccess ? (
            <div>
              <div
                style={{
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '24px',
                  color: '#16a34a',
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              >
                A temporary password has been sent to your email.
              </div>
              <button
                type="button"
                onClick={() => { setIsForgotMode(false); setForgotSuccess(false); setError(''); }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2e496b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Back to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: '24px' }}>
                <label htmlFor="forgot-email" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2e496b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  marginBottom: '12px',
                }}
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>
              <button
                type="button"
                onClick={() => { setIsForgotMode(false); setError(''); }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  color: '#64748b',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
          padding: '40px',
          width: '100%',
          maxWidth: '400px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src={brandLogo} alt="PlaySportUSA" style={{ height: '40px', marginBottom: '16px' }} />
          <h2 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '20px' }}>
            {isSetupMode ? 'Create Your Player Account' : 'Player Login'}
          </h2>
          {isSetupMode && (
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              You&apos;ve been invited to view your messages on PlaySportUSA. Set a password to get started.
            </p>
          )}
        </div>

        {error && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#dc2626',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={isSetupMode ? handleSetup : handleLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="player-email" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
              Email
            </label>
            <input
              id="player-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isSetupMode}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                background: isSetupMode ? '#f9fafb' : '#ffffff',
                boxSizing: 'border-box',
              }}
              required
            />
          </div>

          <div style={{ marginBottom: isSetupMode ? '16px' : '24px' }}>
            <label htmlFor="player-password" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
              Password
            </label>
            <input
              id="player-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
              required
            />
          </div>

          {isSetupMode && (
            <div style={{ marginBottom: '24px' }}>
              <label htmlFor="player-confirm-password" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Confirm Password
              </label>
              <input
                id="player-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: '#2e496b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Please wait...' : null}
            {!loading && isSetupMode ? 'Create Account & View Messages' : null}
            {!loading && !isSetupMode ? 'Log In' : null}
          </button>

          {!isSetupMode && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => { setIsForgotMode(true); setForgotEmail(email); setError(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b5998',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PlayerLoginPage;
